import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ContainerElement } from 'd3';
import { extent } from 'd3-array';
import { Axis, axisLeft } from 'd3-axis';
import { scaleLinear, ScaleLinear, scaleTime, ScaleTime } from 'd3-scale';
import { event as d3Event, select, Selection } from 'd3-selection';
import { curveMonotoneX, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';
import {
    addDays,
    addMonths,
    differenceInDays,
    endOfMonth,
    format,
    subDays,
} from 'date-fns';

import { AxisData } from './axis-data';
import { AxisDataAxisPathPoint } from './axis-data-axis-path-point';
import { AxisLeftTick } from './axis-left-tick';
import { AxisRightTick } from './axis-right-tick';
import { Rect, Util } from './util';

export interface AxisOptions {
    rect?: Rect;
    showYAxisLabel2?: boolean;
    isInsideDialog?: boolean;
    strokeColor?: (path: AxisDataAxisPathPoint[], index: number) => string;
}

export interface AxisPointMouseEvent {
    d: AxisDataAxisPathPoint;
    i: number;
    pointRect: ClientRect;
    event: MouseEvent;
    el: Element;
}

interface YAxisContextIn {
    xScale: ScaleTime<number, number>;
    yScale: ScaleLinear<number, number>;
    yDomain: [number, number];
    xAxisHeight: number;
}

interface YAxisContextOut {
    yScale?: ScaleLinear<number, number>;
    yAxisWidth?: number;
    yAxisWidthRight?: number;
}

interface XAxisContextIn {
    preDraw: boolean;
    dates: Date[];
    xScale: ScaleTime<number, number>;
    yAxisWidth: number;
    yAxisWidthRight: number;
    xAxis: Axis<any>;
    xAxisHeight: number;
}

interface XAxisContextOut {
    xScale?: ScaleTime<number, number>;
    xAxisHeight?: number;
}

interface GridContextIn {
    xScale: ScaleTime<number, number>;
    xAxisHeight: number;
    yScale: ScaleLinear<number, number>;
    yAxisWidthRight: number;
    yAxisWidth: number;
}

interface DataContextIn {
    dates: any[];
    xScale: ScaleTime<number, number>;
    yDomain: [number, number];
    yScale: ScaleLinear<number, number>;
}

interface Context {
    xScale?: ScaleTime<number, number>;
    xAxis?: Axis<any>;
    xAxisHeight?: number;
    yScale?: ScaleLinear<number, number>;
    yAxisWidth?: number;
    yAxisWidthRight?: number;
}

@Component({
    selector: 'ng-chart-chart, [ngChartChart]',
    template: '',
})
export class ChartComponent implements OnChanges, OnDestroy {
    @Input() options: AxisOptions;
    @Input() data: AxisData;

    @Output() pointMouseOver: EventEmitter<AxisPointMouseEvent>;
    @Output() pointMouseOut: EventEmitter<AxisPointMouseEvent>;

    private _svg: Selection<any, any, any, any>;
    private _root: Selection<any, any, any, any>;
    private _xAxisG: Selection<any, any, any, any>;
    private _yAxisG: Selection<any, any, any, any>;
    private _xGridG: Selection<any, any, any, any>;
    private _yGridG: Selection<any, any, any, any>;
    private _shadowG: Selection<any, any, any, any>;
    private _dataG: Selection<any, any, any, any>;
    private _circlesG: Selection<any, any, any, any>;

    private _rectPadded: Rect;
    private _duration: number;
    private _ctx: Context;

    constructor(private readonly _el: ElementRef<HTMLElement>) {
        this.pointMouseOver = new EventEmitter();
        this.pointMouseOut = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('options') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('data')
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._render(true);
        }
    }

    ngOnDestroy(): void {
        this.pointMouseOver.complete();
        this.pointMouseOut.complete();
    }

    private _reset(): void {
        this._duration = 0;
        this._rectPadded = null;
        this._root = null;
        this._svg = null;
        this._el.nativeElement.textContent = '';
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _render(force?: boolean): Promise<void> {
        if ((this.data?.paths?.length ?? 0) === 0) {
            this._reset();
            return;
        }

        const firstRender = !this._svg;

        if (!this._svg) {
            this._svg = select(this._el.nativeElement)
                .append('svg')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('version', '1.1')
                .attr('tabindex', '-1');

            this._root = this._svg.append('g');

            this._xAxisG = this._root.append('g').attr('xAxisG', '');

            this._yAxisG = this._root.append('g');
            // .attr('yAxisG', '')

            this._xGridG = this._root.append('g').attr('xGridG', '');

            this._yGridG = this._root.append('g').attr('yGridG', '');

            this._shadowG = this._root.append('g').attr('yGridG', '');

            this._dataG = this._root.append('g').attr('dataG', '');

            const pointsG = this._root
                .append('g')
                .attr('pointsG', '')
                .style('clip-path', 'url(#clip)');
            this._circlesG = pointsG.append('g').attr('circlesG', '');
        }

        const rect: Rect = {
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };
        rect.width = this.options.rect.width || rect.width;
        rect.height = this.options.rect.height || rect.height;
        rect.top = this.options.rect.top || rect.top;
        rect.right = this.options.rect.right || rect.right;
        rect.bottom = this.options.rect.bottom || rect.bottom;
        rect.left = this.options.rect.left || rect.left;

        this._svg.attr('width', rect.width).attr('height', rect.height);

        const clientRect = (
            this._svg.node() as Element
        ).getBoundingClientRect();
        rect.width = clientRect.width;
        rect.height = clientRect.height;

        const prevRectPadded = this._rectPadded;
        this._rectPadded = {
            width: rect.width - rect.right - rect.left,
            height: rect.height - rect.top - rect.bottom,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };

        this._duration =
            !firstRender && !this.options.isInsideDialog
                ? 5000 /* this.options.transitionDuration */
                : 0;

        /// ///////////////////////////

        this._root.interrupt();
        this._root.selectAll('*').interrupt();

        this._root
            .transition()
            .duration(this._duration)
            .attr('transform', `translate(${rect.left}, ${rect.top})`);

        /// ///////////////////////////

        return this._draw(force, prevRectPadded);
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _draw(force: boolean, prevRectPadded: Rect): Promise<any> {
        if (
            !force &&
            prevRectPadded.width === this._rectPadded.width &&
            prevRectPadded.height === this._rectPadded.height
        ) {
            return;
        }

        /// ///////////////////////////

        // single x-axis flat
        const dates = this.data.paths
            .reduce((p, c) => {
                const d = c.points
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    .filter(
                        (e) =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            e &&
                            e.x &&
                            !p.some((ee) => ee.getTime() === e.x.getTime()),
                    )
                    // .map(e => endOfMonth(subDays(e.x, 1)));
                    .map((e) => endOfMonth(e.x));
                // .map(e => e.x);
                p.push(...d);
                return p;
            }, [])
            .sort((a, b) => a.getTime() - b.getTime());

        // single y-axis flat
        const nums = this.data.paths.reduce((p, c) => {
            const d = c.points
                .sort((a, b) => a.x.getTime() - b.x.getTime())
                .map((e) => e.y);
            p.push(...d);
            return p;
        }, []);

        /// ///////////////////////////

        const yDomain = extent<number>(nums);
        // if (this.options.yDomain) {
        //     yDomain = this.options.yDomain(yDomain);
        // }

        /// ///////////////////////////

        this._ctx = this._ctx || {};

        return (
            this._drawXAxis({
                preDraw: true,
                dates,
                xScale: null,
                yAxisWidth: null,
                yAxisWidthRight: null,
                xAxis: null,
                xAxisHeight: null,
            })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then((result) => {
                    this._ctx = { ...this._ctx, ...result };
                    return this._drawYAxis({
                        xScale: this._ctx.xScale,
                        yScale: this._ctx.yScale,
                        yDomain,
                        xAxisHeight: this._ctx.xAxisHeight,
                    });
                })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then((result) => {
                    this._ctx = { ...this._ctx, ...result };
                    return this._drawXAxis({
                        preDraw: false,
                        dates,
                        xScale: this._ctx.xScale,
                        yAxisWidth: this._ctx.yAxisWidth,
                        yAxisWidthRight: this._ctx.yAxisWidthRight,
                        xAxis: this._ctx.xAxis,
                        xAxisHeight: this._ctx.xAxisHeight,
                    });
                })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    return this._drawGrid({
                        xScale: this._ctx.xScale,
                        xAxisHeight: this._ctx.xAxisHeight,
                        yScale: this._ctx.yScale,
                        yAxisWidthRight: this._ctx.yAxisWidthRight,
                        yAxisWidth: this._ctx.yAxisWidth,
                    });
                })
                .then(() => {
                    this._drawData({
                        dates,
                        xScale: this._ctx.xScale,
                        yDomain,
                        yScale: this._ctx.yScale,
                    });
                })
        );
    }

    /** draw the y axes first to determine the true width of the x axis */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawYAxis(ctx: YAxisContextIn): Promise<YAxisContextOut> {
        return new Promise<YAxisContextOut>((resolve) => {
            let yScale;
            let yAxisWidth = 0;
            let yAxisWidthRight = this._rectPadded.width;

            let exit = false;
            let enter = false;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const that = this;
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const done = () => {
                if (!enter || !exit) return;

                this._yAxisG.attr('display', 'block');
                const width = this._yAxisG.node().getBoundingClientRect().width;
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                yAxisWidth = width + 10;
                this._yAxisG
                    .transition()
                    .duration(that._duration)
                    .attr('transform', `translate(${yAxisWidth}, 0)`)
                    .attr('opacity', 1);
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                resolve && resolve({ yScale, yAxisWidth, yAxisWidthRight });
            };

            const selection = this._root
                .selectAll<SVGGElement, AxisData>('[yAxisG]')
                .data([this.data], () => 'default');

            selection
                .exit()
                .transition()
                .duration(this._duration * 0.5)
                .attr('opacity', 0)
                .remove()
                // .each(function () {
                //     yScales.delete((this as Element).getAttribute('id'));
                // })
                .call(Util.endAll, () => {
                    // TODO revise replace endAll with transition.end
                    exit = true;
                    done();
                });

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const createLeftScale = function () {
                const tickDataPadded = that.data.leftTicks;
                const cs = that._yAxisG
                    .selectAll<SVGGElement, AxisLeftTick>('g')
                    .data(tickDataPadded, (d) => d.tickId);

                yScale = scaleLinear()
                    .domain([0, tickDataPadded.length - 1])
                    .rangeRound([that._rectPadded.height - ctx.xAxisHeight, 0])
                    .nice();

                cs.exit()
                    .transition()
                    .duration(that._duration * 0.5)
                    .attr('opacity', 0)
                    .remove();

                const tickXPadding = 10;
                const colorRectWidth = 30;
                let maxMovementPointsWidth = 0;
                cs.enter()
                    .append('g')
                    // .attr('opacity', 0)
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .attr(
                        'transform',
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        (d) => `translate(0, ${yScale(d.tickId)})`,
                    )
                    .merge(cs)
                    .each(function (d) {
                        // if (d < 1 || d > 9) {
                        const tick = select<SVGGElement, AxisLeftTick>(this);
                        if (d.isExtentTick === true) {
                            tick.append('text')
                                .attr('fill', '#808080')
                                .attr('dy', '0.32em')
                                .text((dd) => dd.label)
                                .attr('x', function () {
                                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                                    const el: SVGElement = this;
                                    return (
                                        (el.getBoundingClientRect().width +
                                            tickXPadding) *
                                        -1
                                    );
                                });
                            return;
                        }
                        tick.append('rect')
                            .attr('display', 'block')
                            .attr('x', (colorRectWidth + tickXPadding) * -1)
                            .attr('y', -(16 / 2))
                            .attr('width', colorRectWidth)
                            .attr('height', 16)
                            .attr('fill', (dd) => dd.color);
                        if (that.options.showYAxisLabel2) {
                            tick.append('text')
                                .attr('movement-points', '')
                                .attr('dy', '0.32em')
                                .text((dd) => dd.label2)
                                .attr('x', function () {
                                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                                    const el: SVGElement = this;
                                    return (
                                        (el.getBoundingClientRect().width +
                                            tickXPadding +
                                            colorRectWidth +
                                            tickXPadding) *
                                        -1
                                    );
                                });
                        }
                    })
                    .call(function () {
                        that._yAxisG
                            .selectAll<SVGTextElement, AxisLeftTick>(
                                '[movement-points]',
                            )
                            .each(function () {
                                maxMovementPointsWidth = Math.max(
                                    maxMovementPointsWidth,
                                    this.getBoundingClientRect().width,
                                );
                            });
                    })
                    .each(function (d) {
                        // if (d < 1 || d > 9) {
                        if (d.isExtentTick === true) {
                            return;
                        }
                        const tick = select<SVGGElement, AxisLeftTick>(this);
                        tick.append('text')
                            .attr('dy', '0.32em')
                            .text((dd) => dd.label)
                            .attr('x', function () {
                                // eslint-disable-next-line @typescript-eslint/no-this-alias
                                const el: SVGElement = this;
                                return (
                                    (el.getBoundingClientRect().width +
                                        tickXPadding +
                                        (that.options.showYAxisLabel2
                                            ? maxMovementPointsWidth +
                                              tickXPadding
                                            : 0) +
                                        colorRectWidth +
                                        tickXPadding) *
                                    -1
                                );
                            });
                    });
            };

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const createRightScale = function () {
                const tickDataPadded = that.data.rightTicks;

                const axisG = that._root.append('g');
                const cs = axisG
                    .selectAll<SVGGElement, AxisRightTick>('g')
                    .data(tickDataPadded, (d) => d.tickId);

                cs.exit()
                    .transition()
                    .duration(that._duration * 0.5)
                    .attr('opacity', 0)
                    .remove();

                const yScale2 = scaleLinear()
                    .domain([0, that.data.leftTicks.length - 1])
                    .rangeRound([that._rectPadded.height - ctx.xAxisHeight, 0]);

                const tickXPadding = 10;
                cs.enter()
                    .append('g')
                    .attr(
                        'transform',
                        (d) =>
                            `translate(0, ${yScale2(
                                d.leftAxisTickRangeCenter,
                            )})`,
                    )
                    .merge(cs)
                    .each(function (d) {
                        // if (d < 1 || d > 9) {
                        if (d.leftAxisTickRangeCenter == null) {
                            // return;
                        }
                        const tick = select<SVGGElement, AxisRightTick>(this);
                        tick.append('text')
                            .attr('movement-points', '')
                            .attr('dy', '0.32em')
                            .text((dd) => dd.label)
                            .attr('x', tickXPadding);
                    });

                const width = axisG.node().getBoundingClientRect().width;
                yAxisWidthRight = yAxisWidthRight - width - 10;
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                axisG.attr('transform', `translate(${yAxisWidthRight}, 0)`);
            };

            if (this.data.rightTicks) {
                createRightScale();
            }

            selection
                .enter()
                .append('g')
                .attr('yAxisG', '')
                .attr('id', () => 'default')
                .attr('opacity', 0)
                .merge(selection)
                .each(createLeftScale)
                .each((d, i, g) => {
                    if (g.length - 1 === i) {
                        enter = true;
                        done();
                    }
                });
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawXAxis(ctx: XAxisContextIn): Promise<XAxisContextOut> {
        return new Promise<XAxisContextOut>((resolve) => {
            // remove existing axis labels
            this._xAxisG.selectAll('g').remove();
            const domain = extent<Date>(ctx.dates);
            const paddedDomain = [
                subDays(endOfMonth(domain[0]), 15),
                addDays(endOfMonth(domain[1]), 15),
            ];

            let xScale = ctx.xScale;
            if (!ctx.preDraw) {
                xScale.rangeRound([ctx.yAxisWidth, ctx.yAxisWidthRight]);
            } else {
                xScale = scaleTime().domain(paddedDomain);
            }

            const tickData: Date[] = [];
            const end = addDays(domain[1], 1);
            let next = endOfMonth(domain[0]);
            while (next < end) {
                tickData.push(next);
                next = endOfMonth(addMonths(subDays(next, 15), 1));
            }

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const drawPath = function (el) {
                const range = xScale.range();
                const path = el.selectAll('path').data([null]);
                path.merge(
                    path
                        .enter()
                        .insert('path', 'g')
                        .attr('d', `M${+range[0]},0H${+range[1]}`)
                        // .insert('line', 'g')
                        // .attr('x1', 0)
                        // .attr('y1', +range[0])
                        // .attr('x2', 0)
                        // .attr('y2', +range[1])
                        .attr('opacity', 0),
                );
            };

            const tickYPadding = 30;
            const tf = timeFormat('%b %Y');
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const drawTick = function () {
                const tick = select<SVGGElement, Date>(this);
                const text = tick
                    .append('text')
                    .attr('movement-dates', '')
                    .attr('dy', '0.32em')
                    .attr('y', tickYPadding)
                    .text((dd) => {
                        if (!this.options?.isInsideDialog) {
                            return format(dd, 'MMM yy');
                        } else {
                            return tf(dd);
                        }
                    });

                Util.wrapText(
                    text.node(),
                    text.node().getBoundingClientRect().height * 2,
                    'top',
                    1,
                );

                text.attr('x', function () {
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const el: SVGElement = this;
                    return (el.getBoundingClientRect().width / 2) * -1;
                });
                text.selectAll('tspan').attr('text-anchor', 'middle');

                // TODO fix Util.wrapText
                text.selectAll('tspan:nth-child(2)').attr('dy', '20px');
            };

            let xAxisHeight: number;
            if (!ctx.preDraw) {
                drawPath(this._xAxisG);

                this._xAxisG.attr('opacity', 0);

                const cs = this._xAxisG
                    .selectAll<SVGGElement, number>('g')
                    .data(tickData);

                cs.exit()
                    .transition()
                    .duration(this._duration * 0.5)
                    .attr('opacity', 0)
                    .remove();

                cs.enter()
                    .append('g')
                    .attr('transform', (d) => `translate(${xScale(d)}, 0)`)
                    .merge(cs)
                    .each(drawTick);

                this._xAxisG
                    .transition()
                    .duration(this._duration)
                    .attr(
                        'transform',
                        `translate(0, ${
                            this._rectPadded.height - ctx.xAxisHeight
                        })`,
                    )
                    .attr('opacity', 1);
            } else {
                const xAxisG = this._root.append('g').attr('opacity', 0);

                drawPath(xAxisG);

                xAxisG
                    .selectAll('g')
                    .data(tickData)
                    .enter()
                    .append('g')
                    .each(drawTick);

                xAxisHeight = (xAxisG.node() as Element).getBoundingClientRect()
                    .height;
                xAxisG.remove();
            }

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            resolve && resolve({ xScale, xAxisHeight });
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawGrid(ctx: GridContextIn): Promise<void> {
        return new Promise<void>((resolve) => {
            const domain = ctx.xScale.domain();

            const tickData: Date[] = [domain[0]];
            const end = addDays(domain[1], 1);
            let next = endOfMonth(domain[0]);
            while (next < end) {
                tickData.push(next);
                tickData.push(addDays(next, 15));
                next = endOfMonth(addMonths(subDays(next, 15), 1));
            }

            const tickXSize = this._rectPadded.height - ctx.xAxisHeight;
            this._xGridG
                .selectAll('g')
                .data(tickData)
                .enter()
                .append('g')
                .attr('transform', (d) => `translate(${ctx.xScale(d)}, 0)`)
                .attr('stroke', 'black')
                .attr('stroke-opacity', '0.5')
                .attr('stroke-dasharray', '2, 2')
                .each(function () {
                    const tick = select<SVGGElement, Date>(this);
                    tick.append('line').attr('y2', tickXSize);
                });

            // TODO consolidate with what is in drawYAxis(), so that tick data length can be referenced
            const tickDataPadded = this.data.leftTicks;

            const yGrid = axisLeft<number>(ctx.yScale)
                .tickSize(-ctx.yAxisWidthRight + ctx.yAxisWidth)
                .tickValues(ctx.yScale.ticks(tickDataPadded.length))
                .tickFormat(() => '');
            // if (this.options.yAxis) {
            //     this.options.yAxis(yGrid, true);
            // }
            this._yGridG
                .transition()
                .duration(this._duration)
                .attr('stroke', 'black')
                .attr('stroke-opacity', '0.5')
                .attr('stroke-dasharray', '2, 2')
                .call(yGrid as any)
                .attr('transform', `translate(${ctx.yAxisWidth}, 0)`);
            // this._yGridG.selectAll('.domain, .tick:nth-child(2), .tick:last-child')
            //     .remove();
            this._yGridG.selectAll('.domain').remove();

            /// //////////////

            if (this.data.rightTicks) {
                const tickDataPadded2 = this.data.rightTicks;
                const yScale2 = scaleLinear()
                    .domain(ctx.yScale.domain())
                    .rangeRound([this._rectPadded.height - ctx.xAxisHeight, 0]);

                this._shadowG
                    .selectAll('g')
                    .data(tickDataPadded2)
                    .enter()
                    .append('rect')
                    .attr(
                        'transform',
                        (d) =>
                            `translate(${ctx.xScale(domain[0])}, ${yScale2(
                                d.leftAxisTickRangeEnd,
                            )})`,
                    )
                    .attr('width', ctx.yAxisWidthRight - ctx.yAxisWidth)
                    .attr('height', (d) => {
                        const lower = yScale2(d.leftAxisTickRangeStart);
                        const upper = yScale2(d.leftAxisTickRangeEnd);
                        return lower - upper;
                    })
                    .attr('fill', (d) => d.color)
                    .attr('opacity', 0.1);
            }

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            resolve && resolve();
        });
    }

    private _drawData(ctx: DataContextIn): void {
        this._root
            .selectAll('[dataG] > *, [circlesG] > *, [textsG] > *')
            .transition()
            .duration(this._duration * 0.5)
            .attr('fill-opacity', 0)
            .attr('stroke-opacity', 0)
            .remove();
        this.data.paths.forEach((path) => {
            const yScale = ctx.yScale;
            const xyLine = line<AxisDataAxisPathPoint>()
                .x((d) => ctx.xScale(d.x))
                .y((d) => yScale(d.y))
                .curve(curveMonotoneX);
            let points: AxisDataAxisPathPoint[];

            // points = path.points.map(e => ({...e, x: endOfMonth(subDays(e.x, 1))}));
            // eslint-disable-next-line prefer-const
            points = path.points.map((e) => ({
                ...e,
                x: subDays(
                    endOfMonth(e.x),
                    differenceInDays(endOfMonth(e.x), e.x) - 1,
                ),
            }));

            if (!points.every((e) => e.y == null)) {
                const path1 = this._dataG
                    .append('path')
                    .datum(points)
                    .attr('axis-id', 'default')
                    .attr('key', path.key);
                path1
                    .style('clip-path', 'url(#clip)')
                    .attr('stroke-opacity', 0)
                    .transition()
                    .delay(this._duration * 0.75)
                    .duration(this._duration * 0.25)
                    .attr('stroke-opacity', 1)
                    .attr('d', xyLine)
                    .attr('fill', 'none')
                    .attr(
                        'stroke',
                        (d, i) => this.options.strokeColor?.(d, i) ?? '#8f8f8f',
                    )
                    .attr(
                        'stroke-width',
                        2 /* this.options.strokeWidth(d, i) */,
                    );

                points.forEach((dd, ii) => {
                    const circle = this._circlesG
                        .append('circle')
                        .attr('axis-id', 'default')
                        .attr('key', path.key)
                        .attr(
                            'transform',
                            `translate(${ctx.xScale(dd.x)}, ${yScale(dd.y)})`,
                        )
                        // .style('pointer-events', 'none')
                        .attr('fill-opacity', 0);
                    circle
                        .transition()
                        .delay(this._duration * 0.75)
                        .duration(this._duration * 0.25)
                        .attr('fill-opacity', 1)
                        // .attr('fill', dd.y == null ? null : this.options.strokeColor(d, i))
                        .attr('fill', dd.color)
                        .attr('r', 8);

                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const that = this;
                    const pointMouseEvent = (
                        d: AxisDataAxisPathPoint,
                        i: number,
                        el: ContainerElement,
                    ): AxisPointMouseEvent => {
                        return {
                            d,
                            i, // TODO revise. -1 to offset the first element in tickDataPadded
                            pointRect: el.getBoundingClientRect(),
                            event: d3Event as MouseEvent,
                            el,
                        };
                    };

                    circle
                        .on('mouseover', function () {
                            circle
                                // .transition()
                                // .duration(250)
                                .attr('r', 10);
                            that.pointMouseOver.emit(
                                pointMouseEvent(
                                    dd,
                                    ii,
                                    this as ContainerElement,
                                ),
                            );
                        })
                        .on('mouseout', function () {
                            circle
                                // .transition()
                                // .duration(250)
                                .attr('r', 8);
                            that.pointMouseOut.emit(
                                pointMouseEvent(
                                    dd,
                                    ii,
                                    this as ContainerElement,
                                ),
                            );
                        });
                });
            }
        });
    }
}
